import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { 
  getAllHeroSections, 
  getHeroSectionsByType, 
  getHeroSectionById,
  createHeroSection, 
  updateHeroSection, 
  deleteHeroSection 
} from '../../api/herosection/HeroSectionApi';

// Hook to fetch all hero sections
export const useHeroSections = () => {
  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery('heroSections', getAllHeroSections, {
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'Failed to fetch hero sections');
    },
    staleTime: 300000, // 5 minutes
    cacheTime: 3600000, // 1 hour
  });

  return {
    heroSections: data?.data || [],
    isLoading,
    error,
    refetchHeroSections: refetch
  };
};

// Hook to fetch hero sections by type
export const useHeroSectionsByType = (type) => {
  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery(['heroSections', type], () => getHeroSectionsByType(type), {
    onError: (error) => {
      toast.error(error?.response?.data?.message || `Failed to fetch ${type} hero sections`);
    },
    staleTime: 300000, // 5 minutes
    cacheTime: 3600000, // 1 hour
    enabled: !!type, // Only run query if type is provided
  });

  return {
    heroSections: data?.data || [],
    isLoading,
    error,
    refetchHeroSections: refetch
  };
};

// Hook to fetch a single hero section by ID
export const useHeroSectionById = (id) => {
  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery(['heroSection', id], () => getHeroSectionById(id), {
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'Failed to fetch hero section');
    },
    staleTime: 300000, // 5 minutes
    cacheTime: 3600000, // 1 hour
    enabled: !!id, // Only run query if id is provided
  });

  return {
    heroSection: data?.data || null,
    isLoading,
    error,
    refetchHeroSection: refetch
  };
};

// Hook to create a new hero section
export const useCreateHeroSection = () => {
  const queryClient = useQueryClient();

  const createHeroSectionMutation = useMutation(createHeroSection, {
    onSuccess: () => {
      toast.success("Hero section created successfully!", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      queryClient.invalidateQueries('heroSections');
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to create hero section';
      toast.error(errorMessage);
    },
  });

  return createHeroSectionMutation;
};

// Hook to update a hero section
export const useUpdateHeroSection = () => {
  const queryClient = useQueryClient();

  const updateHeroSectionMutation = useMutation(updateHeroSection, {
    onSuccess: (data) => {
      toast.success("Hero section updated successfully!", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      queryClient.invalidateQueries('heroSections');
      queryClient.invalidateQueries(['heroSection', data.data.id]);
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to update hero section';
      toast.error(errorMessage);
    },
  });

  return updateHeroSectionMutation;
};

// Hook to delete a hero section
export const useDeleteHeroSection = () => {
  const queryClient = useQueryClient();

  const deleteHeroSectionMutation = useMutation(deleteHeroSection, {
    onSuccess: () => {
      toast.success("Hero section deleted successfully!", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      queryClient.invalidateQueries('heroSections');
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to delete hero section';
      toast.error(errorMessage);
    },
  });

  return deleteHeroSectionMutation;
};